import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUpRight,
  Camera,
  Check,
  Monitor,
  Smartphone,
  Upload,
  X,
} from "lucide-react";

interface LandingPageProps {
  mode: "waitlist" | "launch";
  appName?: string;
  webAppUrl?: string;
}

type BetaPlatform = "ios" | "android";

const lines = [
  ["01", "LIFE", "How you recover, rebuild, and move through change."],
  [
    "02",
    "HEART",
    "What you want, what you protect, and what you pretend not to need.",
  ],
  [
    "03",
    "HEAD",
    "The patterns behind your choices — including the ones you call instinct.",
  ],
  ["04", "FATE", "The pressure points and openings shaping the week ahead."],
];

export function LandingPage({
  mode,
  appName = "Co-Palms",
  webAppUrl = "https://app.co-palms.altuzar.dev",
}: LandingPageProps) {
  const [email, setEmail] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [error, setError] = useState("");
  const [betaPlatform, setBetaPlatform] = useState<BetaPlatform | null>(null);
  const [betaEmail, setBetaEmail] = useState("");
  const [betaStatus, setBetaStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [betaError, setBetaError] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!photo) {
      setPreview(null);
      return;
    }
    const nextPreview = URL.createObjectURL(photo);
    setPreview(nextPreview);
    return () => URL.revokeObjectURL(nextPreview);
  }, [photo]);

  useEffect(() => {
    if (!betaPlatform) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setBetaPlatform(null);
    };
    document.addEventListener("keydown", closeOnEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = "";
    };
  }, [betaPlatform]);

  const convexEndpoint = (path: string) =>
    import.meta.env.VITE_CONVEX_URL
      ? `${import.meta.env.VITE_CONVEX_URL.replace(".convex.cloud", ".convex.site")}${path}`
      : undefined;

  const choosePhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (!selected.type.startsWith("image/")) {
      setError("Choose an image of your open palm.");
      return;
    }
    if (selected.size > 8 * 1024 * 1024) {
      setError("That image is over 8 MB. Choose a smaller one.");
      return;
    }
    setPhoto(selected);
    setError("");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!photo) return setError("First, show us your palm.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return setError("Enter a valid email.");
    setStatus("loading");
    setError("");
    try {
      const endpoint =
        import.meta.env.VITE_WEEKLY_READING_API_ENDPOINT ||
        convexEndpoint("/api/weekly-reading") ||
        import.meta.env.VITE_WAITLIST_API_ENDPOINT;
      if (endpoint) {
        const body = new FormData();
        body.append("email", email);
        body.append("palm", photo);
        const response = await fetch(endpoint, { method: "POST", body });
        if (!response.ok)
          throw new Error("Could not start your weekly reading.");
      } else {
        await new Promise((resolve) => setTimeout(resolve, 650));
      }
      setStatus("success");
    } catch (cause) {
      setStatus("error");
      setError(
        cause instanceof Error
          ? cause.message
          : "Something went wrong. Try again.",
      );
    }
  };

  const openBetaModal = (platform: BetaPlatform) => {
    setBetaPlatform(platform);
    setBetaEmail("");
    setBetaStatus("idle");
    setBetaError("");
  };

  const submitBetaWaitlist = async (event: FormEvent) => {
    event.preventDefault();
    if (!betaPlatform) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(betaEmail)) {
      setBetaError("Enter a valid email.");
      return;
    }

    setBetaStatus("loading");
    setBetaError("");
    try {
      const endpoint = convexEndpoint("/api/beta-waitlist");
      if (!endpoint) throw new Error("Beta signup is temporarily unavailable.");
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: betaEmail.trim().toLowerCase(),
          platform: betaPlatform,
        }),
      });
      if (!response.ok) throw new Error("Could not join the beta list.");
      setBetaStatus("success");
    } catch (cause) {
      setBetaStatus("error");
      setBetaError(
        cause instanceof Error ? cause.message : "Something went wrong.",
      );
    }
  };

  const appActions = (compact = false) => (
    <div className={`app-actions ${compact ? "compact" : ""}`}>
      <button type="button" onClick={() => openBetaModal("ios")}>
        <Smartphone size={16} /> DOWNLOAD iOS
      </button>
      <button type="button" onClick={() => openBetaModal("android")}>
        <Smartphone size={16} /> DOWNLOAD ANDROID
      </button>
      <a className="primary" href={webAppUrl}>
        <Monitor size={16} /> ACCESS WEB <ArrowUpRight size={16} />
      </a>
    </div>
  );

  return (
    <div className="site-shell" data-mode={mode}>
      <header className="site-nav">
        <a className="wordmark" href="#top" aria-label={`${appName} home`}>
          <img src="/co-palms-icon.png" alt="" />
          <span>Co—Palms</span>
        </a>
        <nav aria-label="Main navigation">
          <a href="#reading">Weekly reading</a>
          <a href="#how">How it works</a>
          <a className="nav-cta" href="#reading">
            FREE READING
          </a>
          <a className="nav-web" href={webAppUrl}>
            ACCESS WEB
          </a>
        </nav>
      </header>

      <main id="top">
        <section className="hero-section">
          <div className="perspective-grid" aria-hidden="true" />
          <p className="eyebrow">PALMISTRY FOR LIFE ON EARTH</p>
          <h1>
            Your hand already
            <br />
            knows what’s next.
          </h1>
          <p className="hero-copy">
            {appName} is your palm chart, weekly guidance, reading archive, and
            compatibility app — built for iOS, Android, and the web.
          </p>
          {appActions(true)}
          <a href="#reading" className="weekly-hook">
            OR GET A FREE WEEKLY READING <ArrowDown size={14} />
          </a>

          <div className="phone-stage" aria-label="Preview of the Co-Palms app">
            <div className="phone phone-left">
              <div className="phone-top">
                <span>YOU</span>
                <span>•••</span>
              </div>
              <div className="mini-hand">
                <img src="/co-palms-icon.png" alt="" />
              </div>
              <p className="phone-kicker">TODAY · AUGUST 31</p>
              <p className="phone-quote">
                You’re not avoiding the answer. You’re waiting for it to change.
              </p>
            </div>
            <div className="phone phone-center">
              <div className="phone-top">
                <span>CO—PALMS</span>
                <span>9:41</span>
              </div>
              <p className="phone-greeting">
                Good morning, Maya.
                <br />
                Your hand at a glance:
              </p>
              <p className="phone-headline">
                Say less.
                <br />
                Mean it more.
              </p>
              <div className="reading-rule" />
              <div className="do-dont">
                <div>
                  <b>DO</b>
                  <span>Finish the thing</span>
                  <span>Ask directly</span>
                  <span>Leave early</span>
                </div>
                <div>
                  <b>DON’T</b>
                  <span>Overexplain</span>
                  <span>Text your ex</span>
                  <span>Force timing</span>
                </div>
              </div>
            </div>
            <div className="phone phone-right">
              <div className="phone-top">
                <span>YOUR HAND</span>
                <span>↗</span>
              </div>
              <p className="phone-kicker">THE QUIET ARCHITECT</p>
              <p className="phone-quote">
                You build safety before you admit you need it.
              </p>
              <div className="line-list">
                <span>
                  LIFE <i>Resilient</i>
                </span>
                <span>
                  HEART <i>Guarded</i>
                </span>
                <span>
                  HEAD <i>Exacting</i>
                </span>
                <span>
                  FATE <i>Opening</i>
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="manifesto">
          <p className="section-index">WHAT IS THIS · 01</p>
          <h2>
            Not a horoscope.
            <br />
            Not a personality quiz.
            <br />
            <em>Your actual hand.</em>
          </h2>
          <div className="manifesto-copy">
            <p>
              Most palm readings reduce you to a few generic lines. Co‑Palms
              looks at the shape, depth, forks, breaks, and relationship between
              the marks that are yours alone.
            </p>
            <p>
              We turn that visual pattern into clear language: where your energy
              is going, what deserves attention, and what this particular week
              is asking from you.
            </p>
          </div>
        </section>

        <section className="line-section" id="how">
          <div className="line-intro">
            <p className="section-index">YOUR CHART · 02</p>
            <h2>
              Four lines.
              <br />
              No vague advice.
            </h2>
          </div>
          <div className="line-rows">
            {lines.map(([number, title, copy]) => (
              <article key={title}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{copy}</p>
                <ArrowUpRight size={22} />
              </article>
            ))}
          </div>
        </section>

        <section className="weekly-section" id="reading">
          <div className="weekly-copy">
            <p className="section-index">YOUR FIRST LOOK · 03</p>
            <h2>
              Meet the app
              <br />
              in your inbox.
            </h2>
            <p>
              Start free: send one clear photo of your palm and get a new
              reading every Monday. When you want your full chart, history, and
              compatibility, continue in the Co-Palms app.
            </p>
            <div className="privacy-note">
              <span>YOUR HAND IS PRIVATE.</span> Used only to create your
              readings. Unsubscribe and delete it at any time.
            </div>
          </div>

          <form className="reading-form" onSubmit={submit}>
            {status === "success" ? (
              <div className="success-state">
                <Check size={44} strokeWidth={1.3} />
                <p className="form-step">YOU’RE IN</p>
                <h3>Watch your inbox.</h3>
                <p>Your first weekly palm reading is on its way.</p>
                <button
                  type="button"
                  onClick={() => {
                    setStatus("idle");
                    setPhoto(null);
                    setEmail("");
                  }}
                >
                  READ ANOTHER HAND
                </button>
              </div>
            ) : (
              <>
                <div className="form-heading">
                  <span>01</span>
                  <h3>Show us your palm.</h3>
                </div>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={choosePhoto}
                  hidden
                />
                <button
                  className={`photo-drop ${preview ? "has-preview" : ""}`}
                  type="button"
                  onClick={() => fileInput.current?.click()}
                >
                  {preview ? (
                    <>
                      <img src={preview} alt="Your selected palm" />
                      <span className="replace-photo">
                        <X size={14} /> REPLACE
                      </span>
                    </>
                  ) : (
                    <>
                      <Camera size={48} strokeWidth={1.1} />
                      <b>TAKE OR UPLOAD A PHOTO</b>
                      <span>Open palm · even light · no shadows</span>
                      <Upload size={16} />
                    </>
                  )}
                </button>
                <div className="form-heading email-heading">
                  <span>02</span>
                  <h3>Where should we send it?</h3>
                </div>
                <label className="email-field">
                  <span>EMAIL</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@earth.com"
                    autoComplete="email"
                  />
                </label>
                {error && (
                  <p className="form-error" role="alert">
                    {error}
                  </p>
                )}
                <button
                  className="submit-reading"
                  disabled={status === "loading"}
                >
                  {status === "loading"
                    ? "READING THE LINES…"
                    : "SEND MY WEEKLY READING"}{" "}
                  <ArrowUpRight size={18} />
                </button>
                <p className="consent">
                  By continuing, you agree to receive one weekly reading. No
                  spam. Unsubscribe anytime.
                </p>
              </>
            )}
          </form>
        </section>

        <section className="cross-platform-section">
          <p className="section-index">ONE ACCOUNT · EVERY PLATFORM</p>
          <h2>Pro follows you everywhere.</h2>
          <p>
            Subscribe on web, iOS, or Android and use the same Co-Palms Pro
            access on all three. Your readings and archive stay with your
            account, not your device.
          </p>
          {appActions()}
        </section>

        <section className="closing-section">
          <img src="/co-palms-icon.png" alt="Co-Palms palm symbol" />
          <p>
            YOUR HAND. YOUR CHART.
            <br />
            EVERY SCREEN.
            <br />
            ONE ACCOUNT.
          </p>
          {appActions()}
        </section>
      </main>

      {betaPlatform && (
        <div
          className="beta-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setBetaPlatform(null);
          }}
        >
          <section
            className="beta-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="beta-modal-title"
          >
            <button
              className="beta-modal-close"
              type="button"
              aria-label="Close"
              onClick={() => setBetaPlatform(null)}
            >
              <X size={20} />
            </button>
            {betaStatus === "success" ? (
              <div className="beta-success">
                <Check size={42} strokeWidth={1.3} />
                <p className="section-index">YOU’RE ON THE LIST</p>
                <h2 id="beta-modal-title">Your spot is requested.</h2>
                <p>
                  We’ll email you when your{" "}
                  {betaPlatform === "ios" ? "TestFlight" : "Android testing"}{" "}
                  invite is ready. Accounts added to testing receive one year of
                  Co-Palms Pro.
                </p>
                <button type="button" onClick={() => setBetaPlatform(null)}>
                  DONE
                </button>
              </div>
            ) : (
              <form onSubmit={submitBetaWaitlist}>
                <p className="section-index">FIRST 42 TESTERS</p>
                <h2 id="beta-modal-title">
                  Get one year
                  <br />
                  of Pro free.
                </h2>
                <p className="beta-modal-copy">
                  Join the{" "}
                  {betaPlatform === "ios"
                    ? "iOS TestFlight"
                    : "Android external testing"}{" "}
                  list. The first 42 accounts added to testing get a full year
                  of Co-Palms Pro.
                </p>
                <label className="email-field">
                  <span>EMAIL</span>
                  <input
                    type="email"
                    value={betaEmail}
                    onChange={(event) => setBetaEmail(event.target.value)}
                    placeholder="you@earth.com"
                    autoComplete="email"
                    autoFocus
                  />
                </label>
                {betaError && (
                  <p className="form-error" role="alert">
                    {betaError}
                  </p>
                )}
                <button
                  className="beta-submit"
                  disabled={betaStatus === "loading"}
                >
                  {betaStatus === "loading"
                    ? "REQUESTING…"
                    : betaPlatform === "ios"
                      ? "REQUEST TESTFLIGHT INVITE"
                      : "REQUEST ANDROID INVITE"}
                  <ArrowUpRight size={17} />
                </button>
                <p className="consent">
                  We’ll use this email only for beta access and essential tester
                  updates. See our <a href="/privacy">Privacy Policy</a>.
                </p>
              </form>
            )}
          </section>
        </div>
      )}

      <footer>
        <div className="wordmark">
          <img src="/co-palms-icon.png" alt="" />
          <span>Co—Palms</span>
        </div>
        <p>WEB APP LIVE · NATIVE BETA SOON · © {new Date().getFullYear()}</p>
        <div>
          <a href="/privacy">PRIVACY</a>
          <a href="/legal">TERMS</a>
          <a href="mailto:hello@altuzar.dev">CONTACT</a>
        </div>
      </footer>
    </div>
  );
}

export const WaitlistLanding = (props: Omit<LandingPageProps, "mode">) => (
  <LandingPage {...props} mode="waitlist" />
);
